import React from 'react';
import './HowItWorksPage.css';
import Seo from "./components/Seo";
import {Link} from "react-router-dom";
import NYXLogo from "./img/NYX_NoCode-Home.png";

const HowItWorksPage = () => (
    <div className="how-it-works-page">
        <header className="how-it-works-header">
            <Seo
                title="How It Works — NYX NoCode"
                description="From prompt to page: see how teachers guide, students create, and NYX handles the heavy lifting with safe AI."
            />
            <h1>How NYX NoCode Works</h1>
        </header>
        <div className="hero-section">
            <img src={NYXLogo} alt="NYX NoCode" className="nyx-logo" />
        </div>
        <section className="how-it-works-section">
            <h2>Turning Students into Future-Ready Creators</h2>
            <p>
                With NYX NoCode, learners imagine, prompt, and build real apps, media, and experiences—while teachers guide the way.
            </p>
            <Link to="/how-it-works" className="cta-button">
                👉 Get Started with NYX NoCode
            </Link>
        </section>
        <section className="how-it-works-section">
            <h2>NYX NoCode at a Glance</h2>
            <p>NYX NoCode turns AI into hands‑on learning—whether you want an always‑on classroom tool or a high‑energy event.</p>
            
            <div className="modes-grid">
                <div className="mode-section">
                    <div className="mode-icon" aria-hidden="true">🎓</div>
                    <div className="mode-content">
                        <h3>Classroom Software</h3>
                        <p>Adopt NYX NoCode as a teacher‑centric platform students use all year to build apps, media, and interactive projects with guided prompts and built‑in resources.</p>
                        <span className="mode-tag">Ongoing</span>
                    </div>
                </div>

                <div className="mode-section">
                    <div className="mode-icon" aria-hidden="true">🚀</div>
                    <div className="mode-content">
                        <h3>NoCode Hackathon</h3>
                        <p>Run a time‑boxed challenge where students collaborate, prototype quickly, and showcase creations—perfect for a kickoff, capstone, or STEM/CS celebration.</p>
                        <span className="mode-tag">Event‑based</span>
                    </div>
                </div>
            </div>

            <p className="glance-next">What follows on this page explains how NYX NoCode works, what it means for students and teachers, and the core features that power both delivery models.</p>
        </section>

        <section className="how-it-works-section">
            <h2>What NYX NoCode Means for Students</h2>
            <p>
                NYX NoCode turns every classroom into a creative lab where students don't just consume technology—they
                create with it. By combining the power of multiple AI engines with an easy-to-use no-code interface,
                students can design apps, art, media, and interactive experiences in real time.
            </p>
            
            <h3>Why It Matters</h3>
            <p>
                NYX NoCode isn't just about learning to use AI—it's about learning to think with AI. Students gain hands-on experience in creativity, problem-solving, reverse engineering, and digital storytelling, while teachers build confidence in guiding new technology.
            </p>
            
            <div className="student-learning-features">
                <div className="student-feature">
                    <div className="feature-icon">🤝</div>
                    <div className="feature-content">
                        <h4>AI Collaboration, Not Replacement</h4>
                        <p>Students learn to collaborate with AI as a creative partner rather than having it do their work for them.</p>
                    </div>
                </div>
                <div className="student-feature">
                    <div className="feature-icon">⚖️</div>
                    <div className="feature-content">
                        <h4>Human-AI Balance</h4>
                        <p>Students learn to weigh their personal strengths and the strengths of AI and find their balance.</p>
                    </div>
                </div>
            </div>
            
            <p>
                Together, classrooms transform into innovation labs where everyone can participate, create, and lead in the age of AI.
            </p>
        </section>

        <section className="how-it-works-section">
            <h2>Teachers as Co-Learners and Guides</h2>
            <p>
                NYX NoCode is built on a teacher-centric model that makes AI creation approachable and empowering.
                Educators don't need to be tech experts—NYX makes it simple to guide students through AI-powered creation. Teachers engage as co-learners, exploring alongside their students, modeling curiosity, and building their own digital fluency in real time.
            </p>
            <div className="teachers-features">
                <div className="teacher-feature">
                    <div className="feature-icon">👩‍🏫</div>
                    <div className="feature-content">
                        <h4>Teacher-Led, Classroom-Ready</h4>
                        <p>Teacher-led learning keeps AI safe, structured, and classroom-ready.</p>
                    </div>
                </div>
                <div className="teacher-feature">
                    <div className="feature-icon">📚</div>
                    <div className="feature-content">
                        <h4>Built-in Support System</h4>
                        <p>Help documentation and guides are stored in the Launch Pad with an AI chat advisor for instant assistance.</p>
                    </div>
                </div>
                <div className="teacher-feature">
                    <div className="feature-icon">🔧</div>
                    <div className="feature-content">
                        <h4>Perfect for Tech-Savvy Educators</h4>
                        <p>Experienced educators can leverage their technical expertise to help students reverse-engineer their AI creations, diving deep into how and why their apps work.</p>
                    </div>
                </div>
            </div>
            <p>
                NYX NoCode supports educators at every step, enabling classrooms to embrace project-based learning with AI in ways that are ethical, fun, and transformative.
            </p>
        </section>

        <section className="how-it-works-section">
            <h2>Core Features of NYX NoCode</h2>
            <div className="features-grid">
                <div className="feature-item">
                    <h3>Launch Pad</h3>
                    <p>Get started quickly with interactive help files and an AI chat advisor.</p>
                </div>
                <div className="feature-item">
                    <h3>Doodle Pad</h3>
                    <p>Sketch a doodle and watch it transform into a prompt that powers apps, images, or media.</p>
                </div>
                <div className="feature-item">
                    <h3>3D Pad</h3>
                    <p>Build 3D shapes, apply images and videos, and even feed video back into prompts for new creations.</p>
                </div>
                <div className="feature-item">
                    <h3>Speech Pad</h3>
                    <p>Choose from 41 AI voices to narrate text, generate audio, and save as files.</p>
                </div>
                <div className="feature-item">
                    <h3>Video Pad</h3>
                    <p>Combine multiple videos or combine video with audio to create rich multimedia experiences.</p>
                </div>
                <div className="feature-item">
                    <h3>Prompt Pad</h3>
                    <p>The creative hub: type or speak prompts to generate web apps, video, and interactive experiences. Save, retrieve, and revise your work anytime.</p>
                </div>
                <div className="feature-item">
                    <h3>Gallery</h3>
                    <p>Every creation is automatically saved for easy viewing, sharing, and showcasing.</p>
                </div>
                <div className="feature-item">
                    <h3>Shared Gallery</h3>
                    <p>Students share one of their HTML creations at a time to get constructive, focused reviews from other students, teachers, or judges.</p>
                </div>
                <div className="feature-item">
                    <h3>Ask Justin</h3>
                    <p>Our powerful AI peers inside your HTML to provide feedback and code suggestions.</p>
                </div>
            </div>
        </section>

        <section className="how-it-works-section tech-details">
            <h2>Technical Architecture</h2>
            <div className="tech-box">
                <p>
                    NYX NoCode is an AI-agnostic no-code platform that consolidates multiple powerful AI engines into one teacher-centric environment where educators learn alongside students. With a single prompt, students can build real-world websites and interactive web applications, then extend their creations with AI-generated images (DALL·E), video (Sora), and speech (DeepGram).
                </p>
                <p>
                    Core orchestration is powered by Azure AI and GPT-5 for production and coaching, while custom tools such as the 3D Pad enable students to design original 3D graphics paired with AI-narrated soundtracks. NYX NoCode abstracts these pipelines into a simple interface, while running on GPU-accelerated backends for real-time inference and generative media.
                </p>
            </div>
        </section>
    </div>
);

export default HowItWorksPage;